import { z } from "zod";

/** JWK public key as stored in the device registry (public material only). */
export const publicJwkSchema = z
  .object({
    kty: z.enum(["OKP", "EC"]),
    crv: z.enum(["Ed25519", "P-256"]),
    x: z.string().min(1),
    y: z.string().min(1).optional(),
  })
  .refine((k) => (k.kty === "EC" ? typeof k.y === "string" : k.y === undefined), {
    message: "EC keys require y; OKP keys must not include y",
  });
export type PublicJwk = z.infer<typeof publicJwkSchema>;

export const keyAlgSchema = z.enum(["EdDSA", "ES256"]);

export const deviceInfoSchema = z.object({
  deviceId: z.uuid(),
  publicKey: publicJwkSchema,
  keyAlg: keyAlgSchema,
  product: z.string().min(1).max(60).default("web"),
  platform: z.string().min(1).max(60).default("unknown"),
  name: z.string().min(1).max(120).default("Device"),
  /** The app's own version (Rago apps) or the browser's (Rago Web) — the
   * Security › Sessions badge. Optional: a client without one sends nothing. */
  version: z.string().min(1).max(60).optional(),
});
export type DeviceInfo = z.infer<typeof deviceInfoSchema>;

/**
 * Where a sign-in lands its session: "cookie" (the hosted app — the API also
 * sets the Plex-shaped rago_tv_auth / rago_tv_client_id cookies on the parent
 * domain) or "body" (the server-hosted LAN app, TVs, CLIs: the token comes
 * back in the response only). Every response carries `{sessionToken, user}`.
 */
export const sessionTransportSchema = z.enum(["cookie", "body"]).default("cookie");

export const PASSWORD_MIN = 10;

/** The password rule, one predicate per item on the sign-up checklist — the
 * web renders these as ✕/✓ rows and the API refuses anything that misses one. */
export const PASSWORD_RULES = {
  length: (s: string) => s.length >= PASSWORD_MIN,
  letter: (s: string) => /\p{L}/u.test(s),
  number: (s: string) => /\p{N}/u.test(s),
  special: (s: string) => /[^\p{L}\p{N}]/u.test(s),
} as const;
export type PasswordRule = keyof typeof PASSWORD_RULES;

export const passwordSchema = z
  .string()
  .max(200)
  .refine(PASSWORD_RULES.length, `at least ${PASSWORD_MIN} characters`)
  .refine(PASSWORD_RULES.letter, "a letter")
  .refine(PASSWORD_RULES.number, "a number")
  .refine(PASSWORD_RULES.special, "a special character");
export const usernameSchema = z
  .string()
  .min(3)
  .max(32)
  .regex(/^[a-z0-9][a-z0-9_-]*$/i, "letters, digits, _ and - only");

export const signupRequestSchema = z.object({
  email: z.email(),
  password: passwordSchema,
  username: usernameSchema,
  device: deviceInfoSchema,
  transport: sessionTransportSchema,
});

export const signinRequestSchema = z.object({
  email: z.email(),
  password: z.string().min(1).max(200),
  otp: z.string().min(6).max(8).optional(),
  device: deviceInfoSchema,
  transport: sessionTransportSchema,
});

export const signoutRequestSchema = z.object({
  allDevices: z.boolean().default(false),
});

export const verifyEmailRequestSchema = z.object({ token: z.string().min(1) });
export const forgotPasswordRequestSchema = z.object({ email: z.email() });
export const resetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  newPassword: passwordSchema,
});

export const userSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  username: z.string(),
  displayName: z.string(),
  emailVerified: z.boolean(),
  isAdmin: z.boolean(),
  preferredAudioLang: z.string().nullable(),
  preferredSubtitleLang: z.string().nullable(),
  /** Account creation time ("Joined …" on the profile). Optional: a session
   * signed in before the field shipped carries a user without it until its
   * next GET /v1/me. */
  createdAt: z.iso.datetime().optional(),
  /** The address an email change is waiting on (its mailed link not opened
   * yet); null when none is. On every user the API answers with; absent
   * from the rago_tv_auth cookie, which never carries it (authCookieJson). */
  pendingEmail: z.string().nullable().optional(),
});
export type User = z.infer<typeof userSchema>;

export const patchUserRequestSchema = z.object({
  username: usernameSchema.optional(),
  displayName: z.string().min(1).max(120).optional(),
  preferredAudioLang: z.string().max(16).nullable().optional(),
  preferredSubtitleLang: z.string().max(16).nullable().optional(),
});

/**
 * Email change (Settings › Account): the new address replaces the old one
 * only once the link mailed to it is opened, so a typo can never lock the
 * account out of its sign-in email. Asking takes the account password.
 */
export const emailChangeRequestSchema = z.object({
  newEmail: z.email(),
  currentPassword: z.string().min(1).max(200),
});
export const emailChangeResponseSchema = z.object({
  pendingEmail: z.string(),
  expiresAt: z.iso.datetime(),
});
export type EmailChangeResponse = z.infer<typeof emailChangeResponseSchema>;

export const confirmEmailChangeRequestSchema = z.object({ token: z.string().min(1) });
export const confirmEmailChangeResponseSchema = z.object({ email: z.string() });
export type ConfirmEmailChangeResponse = z.infer<typeof confirmEmailChangeResponseSchema>;

/** Delete account: immediate and final, so it takes the account password. */
export const deleteAccountRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
});

/**
 * An address with its local part hidden — "k•••@example.com" — for the
 * notice an email change sends to the OLD address: enough to recognise one's
 * own new address, nothing to harvest from a mailbox someone else reads.
 */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf("@");
  if (at <= 0) return "•••";
  return `${email.slice(0, 1)}•••${email.slice(at)}`;
}

/**
 * Per-user playback quality defaults (the Plex Web — Quality analog),
 * synced across platforms via /v1/me/settings. Unknown keys pass through
 * so newer clients (mobile apps) can add fields without a broker deploy.
 */
export const qualityCapSchema = z.object({
  maxResolution: z.number().int().positive().nullable().optional(),
  maxBitrateKbps: z.number().int().positive().nullable().optional(),
});
export type QualityCap = z.infer<typeof qualityCapSchema>;

export const playbackSettingsSchema = z.looseObject({
  remoteQuality: qualityCapSchema.optional(),
  playSmallerOriginal: z.boolean().optional(),
  homeUseRecommended: z.boolean().optional(),
  homeQuality: qualityCapSchema.optional(),
  autoAdjust: z.boolean().optional(),
  // Plex Web — Player analogs: client-drawn subtitle appearance + when the
  // server burns subtitles into the video during conversion.
  subtitleColor: z.enum(["white", "yellow", "cyan", "green"]).optional(),
  subtitleSize: z.enum(["tiny", "small", "normal", "large", "huge"]).optional(),
  subtitlePosition: z.enum(["bottom", "top"]).optional(),
  subtitleBackground: z.boolean().optional(),
  burnSubtitles: z.enum(["auto", "image", "always", "never"]).optional(),
});
export type PlaybackSettings = z.infer<typeof playbackSettingsSchema>;

export const patchUserSettingsSchema = z
  .object({
    playback: playbackSettingsSchema.optional(),
    // Account-level "Sync my watch state" consent (ARCHITECTURE §4b). The
    // broker mirrors it to every server the account uses via the grants
    // sync; nothing about the servers' libraries ever comes back up.
    watchStateSync: z.boolean().optional(),
  })
  .refine((v) => v.playback !== undefined || v.watchStateSync !== undefined, {
    message: "Nothing to patch",
  });

export const userSettingsSchema = z.object({
  playback: playbackSettingsSchema,
  watchStateSync: z.boolean(),
  /** When the consent was last switched on; null while off. */
  watchStateSyncAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;

/**
 * Account sessions (ARCHITECTURE §5.1 #1 — Plex-classic since 2026-09-27):
 * ONE opaque token per (user, device), 32 random bytes as base64url, only
 * its sha256 stored, never expiring — revoke-only (sign-out, Security ›
 * Sessions, device removal, password change/reset). No refresh token, no
 * rotation, no proof on this path: nothing a client presents is consumed by
 * presenting it, which is what keeps Plex from ever signing anyone out by
 * accident. The API authenticates by `Authorization: Bearer <sessionToken>`
 * only; the hosted app's cookies (below) are storage, never a credential.
 */
export const SESSION_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
/** sessions.last_used_at is written at most this often. */
export const SESSION_TOUCH_INTERVAL_S = 5 * 60;
/** Revoked rows stay this long (a "signed out 3 days ago" trail), then are swept. */
export const SESSION_REVOKED_RETENTION_S = 90 * 24 * 3600;

/**
 * The hosted app's cookies, Plex-shaped (plex_tv_auth / plex_tv_client_id):
 * set by api.rago.tv on the parent domain (Domain=rago.tv) so app.rago.tv
 * can read them — readable by script on purpose; the SPA reads the token out
 * and sends it as Bearer. Two years like Plex's (browsers clamp to 400 days;
 * every GET /v1/me re-arms it). rago_tv_client_id outlives sign-out.
 */
export const AUTH_COOKIE = "rago_tv_auth";
export const CLIENT_ID_COOKIE = "rago_tv_client_id";
export const AUTH_COOKIE_MAX_AGE_S = 2 * 365 * 24 * 3600;

export const sessionInfoSchema = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime(),
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;

/** GET /v1/me — the bootstrap call: who this bearer is. */
export const meResponseSchema = z.object({ user: userSchema, session: sessionInfoSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Sign-in, sign-up and pin redemption all answer with the token in the body;
 * transport:"cookie" additionally sets the hosted app's cookies. */
export const signInResponseSchema = z.object({ sessionToken: z.string(), user: userSchema });
export type SignInResponse = z.infer<typeof signInResponseSchema>;

export const sessionRevokeReasonSchema = z.enum([
  "signout",
  "signout_all",
  "replaced",
  "device_revoked",
  "password_reset",
  "password_change",
]);
export type SessionRevokeReason = z.infer<typeof sessionRevokeReasonSchema>;

/** A device's live session as GET /v1/devices reports it (Security › Sessions). */
export const deviceSessionSchema = z.object({
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type DeviceSession = z.infer<typeof deviceSessionSchema>;

/** One row of GET /v1/devices: a device holding a live session. A device
 * that signed out is not listed — the list IS the account's sessions. */
export const deviceItemSchema = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  product: z.string(),
  platform: z.string(),
  name: z.string(),
  version: z.string().nullable(),
  lastSeenAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  current: z.boolean(),
  key: z.string(),
  session: deviceSessionSchema,
});
export type DeviceItem = z.infer<typeof deviceItemSchema>;

/**
 * Emailed action links (verify email / password reset / email change). ONE
 * number per purpose: the mint (api routes/auth.ts, lib/emailChange.ts), the
 * mail copy (api lib/mail/templates.ts), the outbox retry cap (api lib/mail/
 * sweeper.ts) and the web copy (app routes/auth/verify.tsx, the Change email
 * dialog) all read it, so the link and every sentence about it can never
 * disagree. Raised 2026-09-19 from 24 h / 1 h: a mail read the next morning
 * must still work.
 */
export const ACTION_LINK_TTL_S = {
  verify_email: 7 * 24 * 3600,
  password_reset: 24 * 3600,
  email_change: 24 * 3600,
} as const;
export type ActionLinkPurpose = keyof typeof ACTION_LINK_TTL_S;

/**
 * A lifetime as the phrase every surface prints: "30 minutes", "1 hour",
 * "24 hours", "7 days". Days only from two days up, so a day-long link reads
 * "24 hours" (what a reader compares against a clock), never "1 day".
 */
export function formatLifetime(seconds: number): string {
  let n: number;
  let unit: string;
  if (seconds >= 2 * 86400 && seconds % 86400 === 0) {
    n = seconds / 86400;
    unit = "day";
  } else if (seconds >= 3600 && seconds % 3600 === 0) {
    n = seconds / 3600;
    unit = "hour";
  } else {
    n = Math.max(1, Math.round(seconds / 60));
    unit = "minute";
  }
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

/** The lifetime phrase for one emailed link purpose. */
export function actionLinkLifetime(purpose: ActionLinkPurpose): string {
  return formatLifetime(ACTION_LINK_TTL_S[purpose]);
}
export const JWT_ISSUER = "https://api.rago.tv";

/** Server-plane audiences and lifetimes (ARCHITECTURE §5.1 types 4/6/8). */
export const SERVER_PLANE_AUDIENCE = "rago-web:servers";
export const RELAY_AUDIENCE = "rago-relay";
export const SERVER_ACCESS_TTL_S = 3600;
export const SERVER_SCOPED_TOKEN_TTL_S = 12 * 3600;
export const RELAY_TOKEN_TTL_S = 3600;
export const ASSERTION_MAX_AGE_S = 300;

/** aud of type-4 server-scoped tokens: rs:{serverId}. */
export function serverAudience(serverId: string): string {
  return `rs:${serverId}`;
}

/** Type-4 server-scoped token claims (ARCHITECTURE §5.1 #4). */
export interface ServerScopedClaims {
  sub: string;
  did: string;
  grant: {
    role: "owner" | "household" | "shared";
    libraryIds: "*" | number[];
    restrictions: Record<string, unknown>;
  };
  gv: number;
  iss: string;
  aud: string;
  iat: number;
  exp: number;
}

// ---- device-link pins (§5.2) + the server-hosted web app's sign-in ----

/**
 * Where a pin-created sign-in may send the browser back to: the server-hosted
 * web app, wherever its owner serves it — an address
 * (`http://192.168.2.97:41400/web/…`), a name (reverse DNS, an own domain
 * behind a reverse proxy) or the direct zone. So any http(s) URL passes;
 * "local" is the app's mode, not the address. The
 * forward URL is stored on the pin at creation and is the only redirect
 * target the hosted /link page will follow; the broker accepts it only from
 * the page it points at (the request's Origin), the consent card names its
 * host, and approval must come from the public IP that created the pin. No
 * credentials, no fragment, ≤ 300 chars. The native apps' return URL is
 * another thing (isAppForwardUrl) and never passes here.
 */
export function isLocalForwardUrl(raw: string): boolean {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > 300) return false;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.username || u.password || u.hash) return false;
  return u.protocol === "http:" || u.protocol === "https:";
}

/**
 * The native apps' return URL: not a page but the scheme their sign-in
 * browser sheet is waiting for. After Approve the hosted /link page sends
 * the browser here (with `?cancelled=1` after Cancel), the system closes the
 * sheet, and the app's proof-signed poll collects the session — the URL
 * itself carries nothing, which is why no same-IP check guards it (Safari
 * may sit behind iCloud Private Relay while the app does not). The broker
 * takes it only from a request without an Origin header: a page in a
 * browser always sends one, so a page can never make a pin that looks like
 * an app's. The consent tap is the boundary, as it is for a TV code.
 */
export const APP_FORWARD_URL = "rago://auth/link";

export function isAppForwardUrl(raw: string | null | undefined): boolean {
  return raw === APP_FORWARD_URL;
}

/** POST /v1/pins body. Unauthenticated: approval by a signed-in user is the
 * authorization; polling requires a Rago-Proof by the submitted key. */
export const createPinRequestSchema = z.object({
  deviceId: z.uuid(),
  devicePublicKey: publicJwkSchema,
  keyAlg: keyAlgSchema,
  product: z.string().min(1).max(60).default("app"),
  platform: z.string().min(1).max(60).default("unknown"),
  name: z.string().min(1).max(120).default("Device"),
  /** See deviceInfoSchema.version — carried onto the device row at redemption. */
  version: z.string().min(1).max(60).optional(),
  /** 12-char code (URL/redirect flows) instead of the 6-char TV code. */
  strong: z.boolean().default(false),
  /** The server-hosted web app's return URL (see isLocalForwardUrl), or
   * APP_FORWARD_URL for the native apps; absent for TV/CLI pins. */
  forwardUrl: z.string().max(300).optional(),
  /** The server the asking page says it is (its own GET /v1/server); kept
   * only with a forwardUrl. A hint for the approval page, which refuses an
   * account with no access before it approves — never the boundary: that is
   * the grant check at the server-token mint and the server's own. */
  serverId: z.uuid().optional(),
});
/** What a client sends (defaults optional). */
export type CreatePinRequest = z.input<typeof createPinRequestSchema>;

export const pinCreatedSchema = z.object({
  id: z.uuid(),
  code: z.string().min(4).max(12),
  /** The hosted approve page; append ?code= for the redirect flow. */
  verifyUrl: z.string(),
  expiresAt: z.iso.datetime(),
});
export type PinCreated = z.infer<typeof pinCreatedSchema>;

/** GET /v1/pins/lookup — the approve page's consent card. */
export const pinLookupSchema = z.object({
  id: z.uuid(),
  product: z.string(),
  platform: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  /** Set only for the server-hosted web app's sign-in; the page redirects here after approval. */
  forwardUrl: z.string().optional(),
  /** The approving browser shares the public IP that created the pin — the
   * redirect flow is approved from the same browser, so a mismatch means a
   * pin someone else created (device-code phishing). */
  sameNetwork: z.boolean(),
  /** Set only when the pin names a server: whether the signed-in account is
   * its owner or has a share on it. false — the page refuses instead of
   * offering Approve. */
  serverAccess: z.boolean().optional(),
});
export type PinLookup = z.infer<typeof pinLookupSchema>;

/** GET /v1/pins/{id} — device poll; the session token travels in the body. */
export const pinPollSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending"), expiresAt: z.iso.datetime() }),
  signInResponseSchema.extend({ status: z.literal("approved") }),
]);
export type PinPoll = z.infer<typeof pinPollSchema>;

/** rago-server GET /v1/me — the verified principal behind the bearer, for
 * the server-hosted app (which has no /v1/resources row to read `owned` from). */
export const localMeSchema = z.object({
  userId: z.string(),
  deviceId: z.string(),
  role: z.enum(["owner", "household", "shared"]),
  libraryIds: z.union([z.literal("*"), z.array(z.number().int())]),
  fromLocal: z.boolean(),
});
export type LocalMe = z.infer<typeof localMeSchema>;
